import { useCallback, useEffect, useRef, useState } from "react";
import {
    emptyCache,
    getDepthPointsFromCache,
    loadDepthPointsForViewport,
    resetDepthPointsCache,
} from "../utils/depthPointLoader";

const DEBOUNCE_MS = 200;

export function useDepthPointsLoader(enabled) {
    const [depthPoints, setDepthPoints] = useState([]);
    const cacheRef = useRef(emptyCache());
    const requestIdRef = useRef(0);
    const debounceRef = useRef(null);
    const lastViewportRef = useRef(null);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const performLoad = useCallback((viewport) => {
        if (!viewport?.bounds || viewport.zoom == null) {
            setDepthPoints([]);
            return;
        }

        const cachedPoints = getDepthPointsFromCache(cacheRef, viewport);
        if (cachedPoints) {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
            setDepthPoints(cachedPoints);
            return;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            const requestId = ++requestIdRef.current;
            loadDepthPointsForViewport(cacheRef, viewport)
                .then((points) => {
                    if (requestIdRef.current !== requestId) return;
                    setDepthPoints(points);
                })
                .catch(() => {
                    if (requestIdRef.current !== requestId) return;
                    setDepthPoints([]);
                });
        }, DEBOUNCE_MS);
    }, []);

    useEffect(() => {
        if (!enabled) {
            resetDepthPointsCache(cacheRef);
            setDepthPoints([]);
            return;
        }

        if (lastViewportRef.current) {
            performLoad(lastViewportRef.current);
        }
    }, [enabled, performLoad]);

    const loadDepthPoints = useCallback((viewport) => {
        if (viewport?.bounds && viewport.zoom != null) {
            lastViewportRef.current = viewport;
        }

        if (!enabledRef.current) {
            return;
        }

        performLoad(viewport);
    }, [performLoad]);

    useEffect(() => () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
    }, []);

    return { depthPoints, loadDepthPoints };
}
